import React, { useEffect, useState } from 'react';
import { Input, Table, Button, DOMHelper, Stack, IconButton, Divider, SelectPicker, Message, useToaster } from 'rsuite';
import type { SortType } from 'rsuite';
import { BaseErrorModel, BaseResponseModel, PagedResponseModel, PaginationFilterRequest, PaginationFilterResult } from '@/models/core.model';
import EditIcon from '@rsuite/icons/Edit';
import CopyIcon from '@rsuite/icons/Copy';
import CheckIcon from '@rsuite/icons/Check';
import { AxiosError } from 'axios';
import { toastErrorMessage, toastSuccessMessage } from '@/utils/toasterMessage';
import TablePagination from '@/components/TablePagination';
import { useNavigate } from 'react-router-dom';
import PageContent from '@/components/PageContent';
import { AdvanceShareStyleVersion, AdvanceShareStyleVersionPaginationFilterRequest, AdvanceShareStyleVersionStatusEnum, AdvanceShareStyleVersionStatusList } from '@/models/advance-share-style-version.model';
import { getAdvanceShareStyleVersion, getPagedAdvanceShareStyleVersions, publishAdvanceShareStyleVersion, upsertAdvanceShareStyleVersion } from '@/services/advance-share-style-version.service';
import { AdvanceShareStyle } from '@/models/advance-share-style.model';

const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;

interface AdvanceShareStyleVersionListProps {
  style?: AdvanceShareStyle;
}

const AdvanceShareStyleVersionList : React.FC<AdvanceShareStyleVersionListProps> = ({style}) => {
  const [filterReq, setFilterReq] = useState<AdvanceShareStyleVersionPaginationFilterRequest>(new PaginationFilterRequest());
  const [pageRes, setPageRes] = useState<PaginationFilterResult>(new PaginationFilterResult());
  const [items, setItems] = useState<AdvanceShareStyleVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [duplicatingId, setDuplicatingId] = useState<number>();
  const [publishingId, setPublishingId] = useState<number>();
  const nav = useNavigate();
  const toaster = useToaster();

  useEffect(() => {
    fetchData(filterReq);
  }, [filterReq]);

  const fetchData = async (req: AdvanceShareStyleVersionPaginationFilterRequest) => {
    setLoading(true);
    try {
      const finalReq = {
        ...req,
        styleId: style?.id ?? req.styleId,
      };         
      const result: PagedResponseModel<AdvanceShareStyleVersion> = await getPagedAdvanceShareStyleVersions(finalReq);
      if (result.success) {
        setItems(result.result);
        setPageRes(pre => ({ 
          ...pre, 
          pageNumber: result.pageNumber, 
          totalRecords: result.totalRecords, 
          pageSize: result.pageSize 
        }));
      }
    } catch (err) {
      setItems([]);
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in fetching advance share style versions:${errorModel?.errorMessage}`);
    }
    finally {
      setLoading(false);
    }
  };

  const handleSortColumn = (sortColumn: string, sortType?: SortType) => {
    setFilterReq(pre => ({
      ...pre, 
      orderBy: sortColumn,
      isAscending: sortType == 'asc'
    }));
  };

  const handleClickDuplicate = (rowData: AdvanceShareStyleVersion) => {
    const handleYesClick = async () => {
      toaster.clear();
      setDuplicatingId(rowData.id);
      try {
        const detail: BaseResponseModel<AdvanceShareStyleVersion> = await getAdvanceShareStyleVersion(rowData.id);
        if (!detail.success || !detail.result) return;
        const result: BaseResponseModel<AdvanceShareStyleVersion> = await upsertAdvanceShareStyleVersion({...detail.result, id: undefined});
        if (result.success) {
          toastSuccessMessage(`Style Version Successfully Duplicated.`);
          if (result.result?.id) {
            nav(`/advance-shares/styles/versions/${result.result.id}`, {state: {styleId: style?.id}});
          } else {
            fetchData(filterReq);
          }
        }
      } catch (err) {
        const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
        toastErrorMessage(`Error in duplicating Style Version:${errorModel?.errorMessage}`);
      }
      finally {
        setDuplicatingId(undefined);
      }
    };

    toaster.push(
      <Message type="info" closable>
        <p style={{marginBottom:10}}>Are you sure you want to <strong>Duplicate Style Version</strong>?</p>
        <div>
          <Button appearance="primary" onClick={handleYesClick}>Yes</Button>
          <Button appearance="subtle" onClick={() => toaster.clear()} style={{ marginLeft:10}}>No</Button>
        </div>
      </Message>,
      { placement: 'topCenter', duration: 0 }
    );
  };

  const handleClickPublish = (rowData: AdvanceShareStyleVersion) => {
    const handleYesClick = async () => {
      toaster.clear();
      setPublishingId(rowData.id);
      try {
        const result: BaseResponseModel<null> = await publishAdvanceShareStyleVersion(rowData.id);
        if (result.success) {
          toastSuccessMessage(`Style Version Successfully Published.`);
          fetchData(filterReq);
        }
      } catch (err) {
        const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
        toastErrorMessage(`Error in publishing Style Version:${errorModel?.errorMessage}`);
      }
      finally {
        setPublishingId(undefined);
      }
    };

    toaster.push(
      <Message type="info" closable>
        <p style={{marginBottom:10}}>Are you sure you want to <strong>Publish Style Version {rowData.version}</strong>?</p>
        <div>
          <Button appearance="primary" onClick={handleYesClick}>Yes</Button>
          <Button appearance="subtle" onClick={() => toaster.clear()} style={{ marginLeft:10}}>No</Button>
        </div>
      </Message>,
      { placement: 'topCenter', duration: 0 }
    );
  };

  return (
    <PageContent>
      <Stack className="table-toolbar" justifyContent="space-between">
        <Button appearance="primary" onClick={() => nav(`/advance-shares/styles/versions/new`, {state: {styleId: style?.id}} )}>
          Add New Version
        </Button>
        <Stack spacing={6}>                 
          <SelectPicker
            label="Status"
            data={AdvanceShareStyleVersionStatusList}
            searchable={false}
            value={filterReq.status ?? null}
            onChange={dataKey => setFilterReq(pre => ({ ...pre, status: dataKey ?? undefined }))} 
          />                    
          <Input 
            placeholder="Keyword" 
            value={filterReq.keyword ?? ''} 
            onChange={dataKey => setFilterReq(pre => ({ ...pre, keyword: dataKey }))} 
          />
        </Stack>
      </Stack>

      <Table
        height={Math.max(getHeight(window) - 350, 400)}
        data={items}
        sortColumn={filterReq.orderBy}
        sortType={filterReq.isAscending ? 'asc' : 'desc'}
        onSortColumn={handleSortColumn}
        loading={loading}
      >            
        <Column flexGrow={0.5} align="center" fullText sortable>
          <HeaderCell>Version</HeaderCell>
          <Cell dataKey="version" />
        </Column>
        <Column flexGrow={4} align="left" sortable>
          <HeaderCell>Prompt</HeaderCell>
          <Cell dataKey="promptTemplate" />
        </Column>
        <Column flexGrow={1} align="center" >
          <HeaderCell>Status</HeaderCell>
          <Cell>
            {(rowData: AdvanceShareStyleVersion) => ( AdvanceShareStyleVersionStatusList.findLast(s => s.value == rowData.status)?.label )}
          </Cell>
        </Column>
        <Column flexGrow={0.9} fixed="right">
          <HeaderCell>...</HeaderCell>
          <Cell style={{ padding: '6px' }}>
            {(rowData : AdvanceShareStyleVersion) => (
              <Stack spacing={6}>
                <IconButton onClick={() => nav(`/advance-shares/styles/versions/${rowData.id}`, {state: {styleId: style?.id}})} icon={<EditIcon />} circle size="sm" />
                <IconButton title="Duplicate" onClick={() => handleClickDuplicate(rowData)} loading={duplicatingId == rowData.id} icon={<CopyIcon />} circle size="sm" />
                {rowData.status == AdvanceShareStyleVersionStatusEnum.Draft && (
                  <IconButton title="Publish" onClick={() => handleClickPublish(rowData)} loading={publishingId == rowData.id} icon={<CheckIcon />} circle size="sm" color="green" appearance="primary" />
                )}
              </Stack>
            )}
          </Cell>
        </Column>

      </Table>
      <Divider />
      <TablePagination 
        totalRecords={pageRes.totalRecords}
        pageSize={pageRes.pageSize}
        activePage={pageRes.pageNumber}
        onChangePage={dataKey => setFilterReq(pre => ({...pre, pageNumber: dataKey}))}
        onChangeLimit={dataKey => setFilterReq(pre => ({...pre, pageSize: dataKey}))}
      />
      
    </PageContent>
  );
};

export default AdvanceShareStyleVersionList;
